<template>
	<v-modal :is-open="isOpen" @close="close">
		<!-- Modal header -->
		<div
			class="relative z-10 flex h-15 items-center overflow-hidden pt-[15px] pr-14 pl-10"
		>
			<!-- Backdrop 1 -->
			<!-- These provide a scroll shadow that matches the current
            background color. Done with 2 different backgrops we alter since CSS
            can't animate the linear gradient we're using -->
			<!-- We make the backdrops slightly less wide on the right side so
            that they don't go over the browser's possible scrollbars that are
            positioned in the further-down fixed-position nav component. Kinda
            hacky but works since these are just backdrops, it doesn't matter
            that they don't cover the entire width 100% -->
			<span
				class="absolute top-0 -z-10 h-15 w-[calc(100%-25px)] bg-[linear-gradient(180deg,var(--background-color)_80%,transparent_100%)] transition-all ease-in-out sm:mr-5"
				:class="{
					'left-0': depth === 0,
					'-left-full': depth > 0,
					'duration-0': skipTransition,
					'duration-300': !skipTransition,
				}"
				:style="{
					'--background-color': backgroundColor,
				}"
			/>
			<!-- Backdrop 2 -->
			<span
				class="absolute top-0 -z-10 h-15 w-[calc(100%-25px)] bg-[linear-gradient(180deg,var(--background-color)_80%,transparent_100%)] transition-all ease-in-out"
				:class="{
					'right-0': depth > 0,
					'-right-full': depth === 0,
					'duration-0': skipTransition,
					'duration-300': !skipTransition,
				}"
				:style="{
					'--background-color': '#FFF',
				}"
			/>

			<!-- Back button -->
			<button
				v-if="depth > 0"
				ref="backButtonRef"
				type="button"
				class="text-left text-xl text-black outline-hidden hover:text-gray-600 ring-focus active:text-gray-400"
				:aria-label="t('aria.nav-back')"
				@click="removeSubmenu"
			>
				<v-icon icon="arrow-left" />
			</button>

			<!-- Close button -->
			<button
				type="button"
				class="ml-auto w-max text-right text-xl outline-hidden ring-focus"
				:class="{
					'text-(--color) hover:text-black/50 active:text-white': depth === 0,
					'text-black hover:text-gray-600 active:text-gray-400': depth > 0,
				}"
				:style="{
					'--color': fontColor,
				}"
				:aria-label="t('aria.nav-close')"
				@click="close"
			>
				<v-icon icon="close" />
			</button>
		</div>

		<!-- Modal content -->
		<div v-if="navigation" class="absolute inset-0 overflow-hidden">
			<div
				class="flex h-full translate-x-(--depth) flex-nowrap bg-(--background-color) text-(--color) transition-transform ease-in-out"
				:class="{
					'duration-0': skipTransition,
					'duration-300': !skipTransition,
				}"
				:style="{
					'--depth': `${depth * -100}%`,
					'--background-color': backgroundColor,
					'--color': fontColor,
				}"
			>
				<v-header-nav-page-main
					:inert="depth !== 0"
					:font-color="fontColor"
					:navigation="navigation"
					@open="addSubmenu"
				/>

				<v-header-nav-page-sub
					v-for="(subMenu, index) in subMenus"
					:key="subMenu.id"
					ref="subPageRefs"
					:sub-menu="subMenu"
					:inert="depth !== index + 1"
					@open="addSubmenu"
				/>
			</div>
		</div>
	</v-modal>
</template>

<script lang="ts" setup>
import "wicg-inert";
import {refAutoReset, watchDebounced} from "@vueuse/core";
import type {ComponentPublicInstance} from "vue";
import type {TreeItem} from "@pkg/shared/server/api/navigation.get";

import {useNavigationData} from "@pkg/shared/composables/use-navigation-data";
import {useStoryblokStory} from "@pkg/shared/composables/use-storyblok-story";

const properties = defineProps<{
	isOpen: boolean;
	section?: string;
}>();

const emit = defineEmits<{
	(event: "close"): void;
}>();

const {t} = useI18n();

const ANIMATION_DURATION = 300;
const skipTransition = refAutoReset(false, ANIMATION_DURATION);

const navigation = await useNavigationData();

const {data: headerContent} = useStoryblokStory("global/header");

const backgroundColor = computed<string | undefined>(() =>
	headerContent.value?.content?.background_color.color === undefined
		? "#EF1C35"
		: headerContent.value?.content?.background_color?.color,
);

const fontColor = computed<string | undefined>(() =>
	headerContent.value?.content?.font_color === undefined
		? "#FFF"
		: headerContent.value?.content?.font_color,
);

const subMenus = ref<TreeItem[]>([]);

const depth = ref(0);

const backButtonRef = ref<HTMLButtonElement | null>(null);
const subPageRefs = ref<ComponentPublicInstance[]>([]);
const focusHistory = shallowRef<HTMLElement[]>([]);

function addSubmenu(item: TreeItem) {
	if (!item.children || item.children.length === 0) {
		return;
	}

	if (document.activeElement instanceof HTMLElement) {
		focusHistory.value.push(document.activeElement);
	}

	subMenus.value.push(item);
	depth.value += 1;

	setTimeout(() => {
		const lastPage = subPageRefs.value.at(-1)?.$el as HTMLElement | undefined;
		const firstLink = lastPage?.querySelector<HTMLElement>(
			"a[href], button:not([disabled])",
		);
		(firstLink ?? backButtonRef.value)?.focus();
	}, ANIMATION_DURATION);
}

function removeSubmenu() {
	const previousFocus = focusHistory.value.pop();

	depth.value -= 1;

	setTimeout(() => {
		subMenus.value.pop();
	}, ANIMATION_DURATION);

	nextTick(() => {
		previousFocus?.focus();
	});
}

watchDebounced(
	() => properties.isOpen,
	(isOpen) => {
		if (!isOpen) {
			depth.value = 0;
		}
	},
	{debounce: ANIMATION_DURATION},
);

watch(
	() => properties.section,
	(section) => {
		if (!navigation) {
			return;
		}

		skipTransition.value = true;

		if (section === "rooms") {
			const roomsTitle = t("pages.rooms");
			const navItem = navigation.find((x) => x.name === roomsTitle);
			if (navItem) {
				addSubmenu(navItem);
			}
		} else if (section === "products") {
			const productsTitle = t("pages.products");
			const navItem = navigation.find((x) => x.name === productsTitle);
			if (navItem) {
				addSubmenu(navItem);
			}
		}
	},
);

function close() {
	emit("close");

	setTimeout(() => {
		subMenus.value = [];
		focusHistory.value = [];
	}, ANIMATION_DURATION);
}

const router = useRouter();

router.beforeEach(() => {
	close();
});
</script>
